/**
 * Speed test — a standalone page (opened from the "Speed test" button in the
 * sidebar, just above "Check for updates"). Benchmarks this machine's on-device
 * render throughput by TIMING THE REAL ENGINE (the spec-019 layered compositor,
 * `engine.composeMockup`) with configurable parameters:
 *   • Resolution — the mockup size per side (512 → 4096 px).
 *   • Images per mockup — design fill layers composited per mockup (2–10).
 * It also times the edit-time bake ops once: the magenta-marker CV detect (always)
 * and the BiRefNet AI matte (`removeBackgroundAI`, DESKTOP-ONLY — shown as N/A on
 * web). Reports the engine impl that ran (native / wasm) and the compute MODE used
 * (CPU — the render engine is CPU-bound — plus detected cores / GPU for context).
 * Pure on-device compute; no network.
 */
import { useState } from "react";
import { useServices } from "../app/context.js";
import {
  MAX_IMAGES,
  MIN_IMAGES,
  RESOLUTIONS,
  runLayeredSpeedBenchmark,
  type SpeedResult,
} from "../stores/benchmark.js";
import { Modal } from "../ui/Modal.js";
import "./speedtest.css";

export function SpeedTestPage({ onClose }: { onClose: () => void }) {
  const { engine } = useServices();
  const [side, setSide] = useState<number>(1024);
  const [images, setImages] = useState<number>(MIN_IMAGES);
  const [result, setResult] = useState<SpeedResult | null>(null);
  const [running, setRunning] = useState(false);

  const run = () => {
    setRunning(true);
    setResult(null);
    // Defer so the "Running…" state paints before the (async) benchmark runs.
    setTimeout(async () => {
      try {
        setResult(await runLayeredSpeedBenchmark(engine, { side, imagesPerMockup: images }));
      } finally {
        setRunning(false);
      }
    }, 50);
  };

  const rating = (s: number) => (s >= 80 ? "Excellent" : s >= 55 ? "Good" : s >= 30 ? "Fair" : "Slow");

  return (
    <Modal title="Speed test" onClose={onClose} testid="speed-test-page" wide>
      <p className="panel-sub">
        Scores this machine’s on-device mock-up throughput — how many mock-ups it can
        generate per minute — by timing the <strong>real layered-compositing engine
        flow</strong> (<code>engine.composeMockup</code>), plus the edit-time bake
        ops (marker detect + AI matte). No network.
      </p>

      <div className="speed-params">
        <label className="field">
          <span>Resolution</span>
          <select
            value={side}
            onChange={(e) => setSide(Number(e.target.value))}
            disabled={running}
            data-testid="speed-resolution"
          >
            {RESOLUTIONS.map((r) => (
              <option key={r} value={r}>{r} × {r}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Images per mock-up ({MIN_IMAGES}–{MAX_IMAGES})</span>
          <input
            type="number"
            min={MIN_IMAGES}
            max={MAX_IMAGES}
            value={images}
            onChange={(e) => setImages(Math.max(MIN_IMAGES, Math.min(MAX_IMAGES, Number(e.target.value) || MIN_IMAGES)))}
            disabled={running}
            data-testid="speed-images"
          />
        </label>
        <button type="button" className="btn btn--primary" onClick={run} disabled={running} data-testid="speed-run">
          {running ? "Running…" : "Run speed test"}
        </button>
      </div>

      {result && (
        <div data-testid="speed-result" className="speedtest__result">
          <div className="kpi-grid">
            <div className="kpi-card">
              <span className="kpi-card__value">{Math.round(result.mockupsPerMin).toLocaleString()}</span>
              <span className="kpi-card__label">mock-ups / min (est.)</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{result.score}</span>
              <span className="kpi-card__label">speed score ({rating(result.score)})</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{result.msPerMockup.toFixed(1)}ms</span>
              <span className="kpi-card__label">per composeMockup</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">{result.mpixPerSec.toFixed(0)}</span>
              <span className="kpi-card__label">megapixels / sec</span>
            </div>
          </div>

          {/* Edit-time bake phase (timed once, outside the compose loop). */}
          <div className="kpi-grid speedtest__bake" data-testid="speed-bake">
            <div className="kpi-card">
              <span className="kpi-card__value">{result.bakeMs.markerDetect.toFixed(1)}ms</span>
              <span className="kpi-card__label">marker detect (CV)</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-card__value">
                {result.bakeMs.removeBackground != null
                  ? `${result.bakeMs.removeBackground.toFixed(1)}ms`
                  : "N/A"}
              </span>
              <span className="kpi-card__label">
                {result.bakeMs.removeBackground != null
                  ? "AI matte (BiRefNet)"
                  : "AI matte — desktop only"}
              </span>
            </div>
          </div>

          {/* Engine impl + compute mode used. */}
          <div className="speed-compute" data-testid="speed-compute">
            {result.engineImpl && (
              <span className="badge badge--info" data-testid="speed-impl">
                Engine: {result.engineImpl === "native" ? "native (desktop)" : "wasm (web)"}
              </span>
            )}
            <span className="badge badge--info">Mode: {result.compute.mode}</span>
            {/* Spec 024: the native engine now composites multi-threaded (bounded to cores−1
                so the UI keeps a core); the web engine stays single-threaded. */}
            {(() => {
              const cores = result.compute.cores ?? null;
              const parallel = result.engineImpl === "native" && (cores == null || cores > 1);
              const threads = parallel && cores != null ? Math.max(1, cores - 1) : 1;
              return (
                <span className="badge badge--success" data-testid="speed-backend" title="Render backend used for this test">
                  {parallel ? `CPU · ${threads} threads (parallel)` : "CPU · single-threaded"}
                </span>
              );
            })()}
            {result.compute.cores != null && (
              <span className="faint">{result.compute.cores} CPU cores</span>
            )}
            {result.compute.gpu && (
              <span className="faint" title="Detected GPU — GPU compositor is a planned follow-on (spec 024 phase 2); not used yet">
                GPU: {result.compute.gpu} <em>(not used yet)</em>
              </span>
            )}
          </div>

          <p className="faint speedtest__note">
            {result.passes.toLocaleString()} composeMockup calls in {(result.elapsedMs / 1000).toFixed(2)}s ·
            {" "}{result.side}×{result.side} layered composite · {result.imagesPerMockup} fill layers/mockup.
            Bake timings are one-shot; AI matte (<code>removeBackgroundAI</code>) is desktop-only.
          </p>
        </div>
      )}
    </Modal>
  );
}
